<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { showSnackbar as showSnackbarHelper } from '@/helper'
import BaseModal from '@/components/BaseModal/index.vue'
import NBFormInput from '@/app/Checkout/components/common/NBFormInput/index.vue'
import NBCheckboxGroup from '@/app/Checkout/components/common/NBCheckboxGroup/index.vue'
import {useBreakpoint} from '../../composables/useBreakpoint';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const store = useStore()
const voucherCodeInput = ref('')
const voucherSelection = ref([])
/** When we sync from cart we set this; only skip the watch if newVal equals this (avoids skipping first user click when updates are batched) */
let pendingSyncSelection = null
/** Track previous selection so we always have a correct diff for add/remove */
let prevSelection = []
const { isMobile } = useBreakpoint()

const cartVouchers = computed(() => store.state.vouchers?.cartVouchers ?? [])
const vouchersPending = computed(() => store.getters['vouchers/isPending'])
const vouchersError = computed(() => store.state.vouchers?.error)
const vouchersSuccess = computed(() => store.state.vouchers?.success)

const showSnackbar = getCurrentInstance()?.appContext?.config?.globalProperties?.showSnackbar ?? showSnackbarHelper

// Show snackbar when voucher error is set (add/remove failed); input error is bound via vouchersError?.msg
watch(
  vouchersError,
  (err) => {
    if (err?.msg && showSnackbar) showSnackbar('error', 'mdi-alert-circle-outline', err.msg)
  },
  { immediate: true }
)

// Show snackbar when voucher add succeeds (mirrors error flow)
watch(
  vouchersSuccess,
  (success) => {
    if (success?.msg && showSnackbar) {
      showSnackbar('success', 'iconfont-check', success.msg)
      store.commit('vouchers/REMOVE_SUCCESS')
    }
  },
  { immediate: true }
)

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// Sync selection from cart (redeemed vouchers): on open, and when cart vouchers change (e.g. after API error so checkboxes match server)
watch(
  cartVouchers,
  (vouchers) => {
    if (!vouchers?.length) return
    const synced = vouchers.filter((o) => o.redeemed).map((o) => o.id)
    pendingSyncSelection = synced.slice()
    voucherSelection.value = synced
  },
  { immediate: true }
)

// On checkbox checked: add voucher by code. On unchecked: remove voucher by code.
// Only skip when this run is from our sync (same as pendingSyncSelection), not when user already changed selection (batched update).
watch(
  voucherSelection,
  (newVal) => {
    const nextVal = Array.isArray(newVal) ? newVal : []
    const isSyncRun =
      pendingSyncSelection &&
      nextVal.length === pendingSyncSelection.length &&
      nextVal.every((id) => pendingSyncSelection.includes(id))
    if (isSyncRun) {
      pendingSyncSelection = null
      prevSelection = [...nextVal]
      return
    }
    // If we had a pending sync but got user's value first (batched), treat synced as previous
    if (pendingSyncSelection) {
      prevSelection = [...pendingSyncSelection]
    }
    pendingSyncSelection = null
    const vouchers = cartVouchers.value
    const getVoucherById = (id) => vouchers.find((v) => v.id === id)
    const addedIds = nextVal.filter((id) => !prevSelection.includes(id))
    const removedIds = prevSelection.filter((id) => !nextVal.includes(id))
    prevSelection = [...nextVal]
    const addedCodes = addedIds.map((id) => getVoucherById(id)?.code).filter(Boolean)
    const removedCodes = removedIds.map((id) => getVoucherById(id)?.code).filter(Boolean)
    if (addedCodes.length) store.dispatch('vouchers/addCartVouchers', addedCodes)
    if (removedCodes.length) store.dispatch('vouchers/removeCartVouchers', removedCodes)
  },
  { deep: true }
)

async function onRedeemClick() {
  const code = voucherCodeInput.value?.trim()
  if (!code) return
  try {
    await store.dispatch('vouchers/addCartVouchers', [code])
    voucherCodeInput.value = ''
  } catch (_) {
    // Error shown via vouchersError on NBFormInput
  }
}
</script>
